import { chatRowClassName } from "@app/components/assistant/conversation/ChatsList";
import type {
  ContactsView,
  DirectoryPerson,
} from "@app/components/people/contactsDirectory";
import {
  contactsHref,
  contactsViews,
  isContactsView,
  peopleAgentMatchesSearch,
  personHref,
  selectedPerson,
  useContactsSelection,
  useDirectoryPeople,
} from "@app/components/people/contactsDirectory";
import { SidebarContext } from "@app/components/sparkle/SidebarContext";
import { useAppRouter } from "@app/lib/platform";
import { getSkillAvatarIcon } from "@app/lib/skill";
import { agentDisplayName } from "@app/types/assistant/agent";
import { agentAvatarVisual } from "@app/types/assistant/avatar";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Avatar,
  ButtonsSwitch,
  ButtonsSwitchList,
  LoadingBlock,
  NavigationListItem,
} from "@dust-tt/sparkle";
import type { ReactNode } from "react";
import { useContext } from "react";

const sectionLabelClassName =
  "flex h-8 items-center px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground";

function personSubtitle(person: DirectoryPerson): string | undefined {
  if (person.kind === "member") {
    return person.member.email;
  }
  const { role, company } = person.wiki;
  return [role, company].filter(Boolean).join(" · ") || undefined;
}

function personImage(person: DirectoryPerson): string | undefined {
  return person.kind === "member"
    ? (person.member.image ?? undefined)
    : (person.wiki.photoUrl ?? undefined);
}

interface ContactsListProps {
  owner: LightWorkspaceType;
  titleFilter: string;
}

/**
 * The Contacts list pane: one switch between people, agents and skills, then
 * the rows for that kind. Selecting a row only changes the URL; the page reads
 * the same selection to fill its main pane and side panel.
 */
export function ContactsList({ owner, titleFilter }: ContactsListProps) {
  const router = useAppRouter();
  const selection = useContactsSelection();
  const { setSidebarOpen } = useContext(SidebarContext);
  const {
    people,
    agents,
    skills,
    isPeopleLoading,
    isAgentsLoading,
    isSkillsLoading,
  } = useDirectoryPeople(owner);

  const needle = titleFilter.trim().toLowerCase();
  const closeSheet = () => setSidebarOpen(false);

  const row = ({
    key,
    label,
    description,
    href,
    selected,
    avatar,
  }: {
    key: string;
    label: string;
    description?: string;
    href: string;
    selected: boolean;
    avatar: ReactNode;
  }) => (
    <NavigationListItem
      key={key}
      label={label}
      description={description}
      href={href}
      shallow
      selected={selected}
      className={chatRowClassName}
      avatar={avatar}
      onClick={closeSheet}
    />
  );

  const renderPeople = () => {
    const selectedPersonKey = selectedPerson(people, selection)?.key;
    const matching = people.filter(
      (person) =>
        needle === "" ||
        person.name.toLowerCase().includes(needle) ||
        (personSubtitle(person) ?? "").toLowerCase().includes(needle)
    );
    const groups: { label: string; rows: DirectoryPerson[] }[] = [
      {
        label: "In your workspace",
        rows: matching.filter((person) => person.kind === "member"),
      },
      {
        label: "From your mail",
        rows: matching.filter((person) => person.kind === "contact"),
      },
    ];
    return groups
      .filter((group) => group.rows.length > 0)
      .map((group) => (
        <div key={group.label} className="flex flex-col gap-1">
          <span className={sectionLabelClassName}>{group.label}</span>
          {group.rows.map((person) =>
            row({
              key: person.key,
              label: person.name,
              description: personSubtitle(person),
              href: personHref(owner.sId, person),
              selected: person.key === selectedPersonKey,
              avatar: (
                <Avatar
                  size="sm"
                  visual={personImage(person)}
                  name={person.name}
                  isRounded
                />
              ),
            })
          )}
        </div>
      ));
  };

  const renderAgents = () =>
    agents
      .filter(
        (agent) => needle === "" || peopleAgentMatchesSearch(agent, needle)
      )
      .map((agent) =>
        row({
          key: agent.sId,
          label: agentDisplayName(agent),
          description: agent.description || undefined,
          href: contactsHref(owner.sId, { view: "agents", id: agent.sId }),
          selected: selection.id === agent.sId,
          avatar: (
            <Avatar
              size="sm"
              visual={agentAvatarVisual(agent)}
              name={agentDisplayName(agent)}
            />
          ),
        })
      );

  const renderSkills = () =>
    skills
      .filter(
        (skill) =>
          needle === "" ||
          skill.name.toLowerCase().includes(needle) ||
          skill.userFacingDescription.toLowerCase().includes(needle)
      )
      .map((skill) => {
        const SkillAvatar = getSkillAvatarIcon(skill);
        return row({
          key: skill.sId,
          label: skill.name,
          description: skill.userFacingDescription || undefined,
          href: contactsHref(owner.sId, { view: "skills", id: skill.sId }),
          selected: selection.id === skill.sId,
          avatar: <SkillAvatar name={skill.name} size="sm" />,
        });
      });

  const isLoading = {
    people: isPeopleLoading,
    agents: isAgentsLoading,
    skills: isSkillsLoading,
  }[selection.view];
  const rows = {
    people: renderPeople,
    agents: renderAgents,
    skills: renderSkills,
  }[selection.view]();

  return (
    <div className="flex flex-col gap-3 px-sidebar-side-spacing pt-2">
      <ButtonsSwitchList
        size="xs"
        aria-label="Show"
        className="w-full [&>*]:flex-1"
        value={selection.view}
        onValueChange={(next) => {
          if (isContactsView(next)) {
            void router.push(
              contactsHref(owner.sId, { view: next satisfies ContactsView })
            );
          }
        }}
      >
        {contactsViews.map((view) => (
          <ButtonsSwitch key={view.id} value={view.id} label={view.label} />
        ))}
      </ButtonsSwitchList>
      <div className="flex flex-col gap-0.5 pb-4">
        {isLoading ? (
          <div className="flex flex-col gap-2 px-2">
            <LoadingBlock className="h-11 w-full rounded-xl" />
            <LoadingBlock className="h-11 w-full rounded-xl" />
            <LoadingBlock className="h-11 w-full rounded-xl" />
          </div>
        ) : Array.isArray(rows) && rows.length === 0 ? (
          <p className="copy-sm px-2 py-6 text-center text-muted-foreground">
            {needle ? "Nothing matches your search." : "Nothing here yet."}
          </p>
        ) : (
          rows
        )}
      </div>
    </div>
  );
}
